import { observable, action } from 'mobx';
import { RootStore } from '../store';

/**
 * Held in one place rather than at each save site: the dashboard's 19 save
 * sites all funnel through `api.restaurant_update`, so one dialog renders it.
 */
/**
 * What a refused save says in a toast.
 *
 * The server sends its own sentence, but it is a fallback for callers that do
 * not render the dialog. Owning the wording here keeps the two lines apart —
 * what happened, then what it means for the work in front of you — and keeps
 * markup out of an API message field.
 */
export const MENU_CONFLICT_MESSAGE =
	'Someone else changed this menu since you opened it.<br>Your changes have not been saved.';

export interface MenuConflictState {
	restaurant_id: string;
	last_updated_by?: T.API.MenuAuthorStamp;
	menu_version?: number;
	/**
	 * Repeats the refused save against the version the server reported.
	 *
	 * Supplied by the save site, because only it can finish the job — commit
	 * the edited restaurant, tell the form it succeeded. A site that cannot
	 * retry registers nothing and the dialog simply does not offer it.
	 */
	overwrite?: () => Promise<void>;
}

export class MenuConflictStore {
	private store: RootStore;

	@observable active: MenuConflictState | null = null;

	/** Reload discards unsaved edits, so the dialog stays put and disabled. */
	@observable working: boolean = false;

	constructor(store: RootStore) {
		this.store = store;
	}

	@action raise(restaurant_id: string, error: T.API.MenuConflictResponse) {
		this.active = {
			restaurant_id,
			last_updated_by: error.last_updated_by,
			menu_version: error.menu_version,
		};
	}

	/**
	 * Lets the save that was just refused be repeated over the other person's.
	 *
	 * Called by the save site straight after the refusal, so the dialog can
	 * offer it. Overwriting has to be something the operator asks for: before
	 * this, dismissing the dialog was enough, because the refusal quietly
	 * recorded the server's version and the next save sailed through.
	 */
	@action offerOverwrite(overwrite: () => Promise<void>) {
		if (this.active) this.active = { ...this.active, overwrite };
	}

	/** Saves the operator's version over the one that got there first. */
	async saveMine() {
		const conflict = this.active;
		if (!conflict?.overwrite) return;

		this.setWorking(true);
		try {
			await conflict.overwrite();
			this.dismiss();
		} catch (e) {
			// Left open on purpose. The retry can be refused in turn — someone
			// saved again while the dialog was up — and that raises a fresh
			// conflict the operator still has to answer.
			this.setWorking(false);
		}
	}

	@action dismiss() {
		this.active = null;
		this.working = false;
	}

	@action setWorking(working: boolean) {
		this.working = working;
	}

	/** Destructive despite the safe-sounding button, which is why the dialog says so. */
	async reload() {
		const conflict = this.active;
		if (!conflict) return;

		this.setWorking(true);
		try {
			const restaurant = await this.store.api.restaurant_find(conflict.restaurant_id);
			if (restaurant && (restaurant as any)._id) {
				this.store.updateRestaurant(restaurant as T.Schema.Restaurant.RestaurantSchema);
			}
			this.dismiss();
		} finally {
			this.setWorking(false);
		}
	}
}
